/**
 * CRM API
 *
 * Handles all CRM import-related API calls, plus the deal pipelines/stages
 * reference (Phase B, B-6) that powers the stage-bindings authoring UI and
 * the stage badge on the analysis surface.
 */

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import { INSTALLATION_CHANGED_REFUSALS, withInstallation } from '../crm-installation';
import {
  CRM_PROCESSING_INACTIVE_CODE,
  crmStageNamesResponseSchema,
  crmStagesReferenceResponseSchema,
  putStageSemanticsResponseSchema,
  salesMethodologyListResponseSchema,
  salesMethodologyResponseSchema,
} from '@aica/api-types/schemas';
import type {
  CrmStageNamesResponse,
  CrmStagesReferenceResponse,
  PutSalesMethodologyRequest,
  PutStageSemanticsRequest,
  PutStageSemanticsResponse,
  SalesMethodologyListResponse,
  SalesMethodologyResponse,
} from '@aica/api-types';
import type {
  CRMImportEstimateRequest,
  CRMImportEstimateResponse,
  CRMImportHistoryResponse,
  CRMImportInitiateRequest,
  CRMImportInitiateResponse,
  ImportJobStatusResponse,
  SuccessResponse,
} from '../types';

/**
 * The stages reference of a connected CRM whose processing the customer paused
 * is an answer the tab explains, not a failure to report.
 */
const STAGES_REFUSALS = [{ status: 409, code: CRM_PROCESSING_INACTIVE_CODE }] as const;

export class CrmApi extends BaseApiClient {
  async getCRMImportHistory(
    orgId: string,
    limit = 50
  ): Promise<CRMImportHistoryResponse> {
    const query = `?organizationId=${encodeURIComponent(orgId)}&limit=${limit}`;
    return this.request<CRMImportHistoryResponse>(
      `${API_ENDPOINTS.CRM_IMPORT_HISTORY}${query}`
    );
  }

  async initiateCRMImport(
    data: CRMImportInitiateRequest
  ): Promise<CRMImportInitiateResponse> {
    return this.request<CRMImportInitiateResponse>(API_ENDPOINTS.CRM_IMPORT_INITIATE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * How many records the range holds and what importing them will cost —
   * answered while the dialog is still open, so the operator approves a number
   * rather than a date range. Creates nothing.
   */
  async estimateCRMImport(
    data: CRMImportEstimateRequest
  ): Promise<CRMImportEstimateResponse> {
    return this.request<CRMImportEstimateResponse>(API_ENDPOINTS.CRM_IMPORT_ESTIMATE, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Get detailed status of an import job including progress, costs, and errors.
   * Use with polling (refetchInterval) for real-time progress updates.
   */
  async getImportStatus(jobId: string): Promise<ImportJobStatusResponse> {
    return this.request<ImportJobStatusResponse>(API_ENDPOINTS.CRM_IMPORT_STATUS(jobId));
  }

  async cancelCRMImport(jobId: string): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.CRM_IMPORT_CANCEL(jobId), {
      method: 'POST',
    });
  }

  /**
   * Get the org's deal pipelines/stages reference, each stage annotated with
   * the phase its calls are stored with (`suggestedSemantic`, #1206) and
   * where that phase came from. `{ data: [] }` means no CRM with a pipeline
   * reference is connected; a CRM that did not answer is a 503 error, never
   * an empty list (#1206); a connected CRM whose processing the customer
   * paused (Uspacy) is a 409 `crm_processing_inactive`.
   *
   * `classify` — only the deal stages settings tab: stages without a cached
   * AI guess get one now (a model call, seconds, for an org with stage-based
   * selection on). Every other screen reads the cached guesses only.
   */
  async getStages(
    options: { classify?: boolean } = {}
  ): Promise<CrmStagesReferenceResponse> {
    return this.validatedRequest(
      options.classify
        ? `${API_ENDPOINTS.CRM_STAGES}?classify=1`
        : API_ENDPOINTS.CRM_STAGES,
      crmStagesReferenceResponseSchema,
      { expectedRefusals: STAGES_REFUSALS }
    ) as Promise<CrmStagesReferenceResponse>;
  }

  /**
   * The stage names of EVERY active CRM of the organization, one list per CRM
   * (#2135) — for the screens whose rows come from several CRMs. Refusals as
   * `getStages`: 409 `crm_processing_inactive` when every connected CRM is
   * paused, 503 when none answered.
   */
  async getStageNames(): Promise<CrmStageNamesResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.CRM_STAGE_NAMES,
      crmStageNamesResponseSchema,
      { expectedRefusals: STAGES_REFUSALS }
    ) as Promise<CrmStageNamesResponse>;
  }

  /**
   * Set (`semantic`) or clear (`null`) the customer's phase of stages (#1206).
   * Calls already recorded at those stages get the new phase in the same
   * request; `rewritten` says how many. `installationId` — the install whose
   * stages the tab shows (#2139): 409 `crm_installation_changed` when it is not
   * the org's now. Left out when the server named none.
   */
  async putStageSemantics(
    body: PutStageSemanticsRequest,
    installationId?: string
  ): Promise<PutStageSemanticsResponse> {
    return this.validatedRequest(
      withInstallation(API_ENDPOINTS.CRM_STAGE_SEMANTICS, installationId),
      putStageSemanticsResponseSchema,
      {
        method: 'PUT',
        body: JSON.stringify(body),
        expectedRefusals: INSTALLATION_CHANGED_REFUSALS,
      }
    );
  }

  /**
   * The sales methodology of every pipeline that has one stored (#766). A
   * pipeline missing from the list has the default (BANT + SPIN, no company
   * rules). `installationId` — the install whose pipelines the tab shows
   * (#2118): 409 `crm_installation_changed` when it is not the org's now.
   */
  async listSalesMethodologies(
    installationId?: string
  ): Promise<SalesMethodologyListResponse> {
    return this.validatedRequest(
      withInstallation(API_ENDPOINTS.CRM_SALES_METHODOLOGIES, installationId),
      salesMethodologyListResponseSchema,
      { expectedRefusals: INSTALLATION_CHANGED_REFUSALS }
    );
  }

  /**
   * Set the framework and the company rules of one pipeline (#766).
   * `installationId` — as for the list. Left out when the server named none.
   */
  async putSalesMethodology(
    pipelineId: string,
    body: PutSalesMethodologyRequest,
    installationId?: string
  ): Promise<SalesMethodologyResponse> {
    return this.validatedRequest(
      withInstallation(
        API_ENDPOINTS.CRM_PIPELINE_SALES_METHODOLOGY(pipelineId),
        installationId
      ),
      salesMethodologyResponseSchema,
      {
        method: 'PUT',
        body: JSON.stringify(body),
        expectedRefusals: INSTALLATION_CHANGED_REFUSALS,
      }
    );
  }

  /** Back to the default methodology for one pipeline (#766); `installationId` — as for the PUT. */
  async resetSalesMethodology(
    pipelineId: string,
    installationId?: string
  ): Promise<SalesMethodologyResponse> {
    return this.validatedRequest(
      withInstallation(
        API_ENDPOINTS.CRM_PIPELINE_SALES_METHODOLOGY(pipelineId),
        installationId
      ),
      salesMethodologyResponseSchema,
      { method: 'DELETE', expectedRefusals: INSTALLATION_CHANGED_REFUSALS }
    );
  }
}
